/**
 * sceneLighting.js
 *
 * Shared Three.js scene setup utilities for the design studio.
 * Provides consistent lighting, wall backdrop, shadow configuration,
 * and telephoto perspective camera across single-frame and multi-frame views.
 */

import * as THREE from 'three'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'

// ---------------------------------------------------------------------------
// Shadow-enabled renderer + PCSS
// ---------------------------------------------------------------------------

/**
 * Configure a WebGLRenderer for BasicShadowMap.
 * BasicShadowMap keeps shadowMap as sampler2D with float depth in .r,
 * required for PCSS to read raw depth values.
 * (PCFShadowMap uses sampler2DShadow in WebGL2/r152+, blocking PCSS depth reads.)
 * @param {THREE.WebGLRenderer} renderer
 */
export function setupShadowRenderer(renderer) {
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.BasicShadowMap
}

/**
 * Inject PCSS (Percentage-Closer Soft Shadows) into Three.js's shadow shader.
 * Written for Three.js r152+ / WebGL2 where depth is a float .r value, not RGBA-packed.
 * Patches THREE.ShaderChunk globally — safe to call from multiple components, applies once.
 *
 * @param {object} opts
 * @param {number} opts.size    - virtual light source size; larger = softer far shadows (default 35)
 * @param {number} opts.samples - Vogel disk samples per pass; higher = less grain (default 16)
 */
let _pcssApplied = false
export function setupPCSS({ size = 90, samples = 16 } = {}) {
  if (_pcssApplied) return
  _pcssApplied = true

  // Injected into THREE.ShaderChunk.shadowmap_pars_fragment.
  // BasicShadowMap: shadowMap is sampler2D, depth stored as float in .r channel.
  // PI2 is available from Three.js's math_pars_fragment chunk.
  const glsl = `
    vec2 pcssVogelDisk( int idx, float phi ) {
      float r = sqrt( float( idx ) + 0.5 ) / sqrt( float( ${samples} ) );
      float theta = float( idx ) * 2.399963 + phi;
      return vec2( cos( theta ), sin( theta ) ) * r;
    }

    float pcssBlockerSearch( sampler2D shadowMap, vec2 uv, float compare, float phi, float searchRadius ) {
      float sum = 0.0; float count = 0.0;
      for ( int i = 0; i < ${samples}; i ++ ) {
        vec2 offset = pcssVogelDisk( i, phi ) * searchRadius;
        float depth = texture( shadowMap, uv + offset ).r;
        if ( depth < compare ) { sum += depth; count += 1.0; }
      }
      return count > 0.0 ? sum / count : -1.0;
    }

    float pcssPCF( sampler2D shadowMap, vec2 uv, float z, float filterRadius, float phi ) {
      float shadow = 0.0;
      for ( int i = 0; i < ${samples}; i ++ ) {
        vec2 offset = pcssVogelDisk( i, phi ) * filterRadius;
        shadow += step( z, texture( shadowMap, uv + offset ).r );
      }
      return shadow / float( ${samples} );
    }

    float pcss( sampler2D shadowMap, vec4 shadowCoord, float shadowIntensity, float shadowMapWidth ) {
      float texelSize = 1.0 / shadowMapWidth;
      vec2 uv = shadowCoord.xy;
      float z = shadowCoord.z;
      float phi = fract( sin( dot( gl_FragCoord.xy, vec2( 12.9898, 78.233 ) ) ) * 43758.5453 ) * PI2;
      float avgBlocker = pcssBlockerSearch( shadowMap, uv, z, phi, texelSize * float( ${size} ) );
      if ( avgBlocker < 0.0 ) return 1.0;
      float penumbra = ( z - avgBlocker ) / avgBlocker;
      float filterRadius = texelSize * ( 1.0 + penumbra * float( ${size} ) );
      return mix( 1.0, pcssPCF( shadowMap, uv, z, filterRadius, phi ), shadowIntensity );
    }
  `

  // Inject helpers and replace the BasicShadowMap depth comparison with our PCSS call.
  // Target string is unique to the BasicShadowMap getShadow body in Three.js r152+.
  THREE.ShaderChunk.shadowmap_pars_fragment = THREE.ShaderChunk.shadowmap_pars_fragment
    .replace('#ifdef USE_SHADOWMAP', '#ifdef USE_SHADOWMAP\n' + glsl)
    .replace(
      'float depth = texture2D( shadowMap, shadowCoord.xy ).r;',
      'return pcss( shadowMap, shadowCoord, shadowIntensity, shadowMapSize.x );\n\t\t\t\tfloat depth = texture2D( shadowMap, shadowCoord.xy ).r;'
    )
}

// ---------------------------------------------------------------------------
// IBL environment
// ---------------------------------------------------------------------------

/**
 * Apply a RoomEnvironment IBL to the scene for PBR ambient lighting.
 * Sets scene.environment so all MeshStandardMaterial objects pick up
 * realistic ambient reflections without needing hemisphere/fill lights.
 * Does NOT cast shadows — pair with a shadow-casting directional/spot light.
 *
 * Call once after creating the renderer and scene.
 *
 * @param {THREE.WebGLRenderer} renderer
 * @param {THREE.Scene} scene
 */
export function setupSceneEnvironment(renderer, scene) {
  const pmremGenerator = new THREE.PMREMGenerator(renderer)
  scene.environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture
  scene.environmentIntensity = 0.6
  pmremGenerator.dispose()
}

// ---------------------------------------------------------------------------
// Wall
// ---------------------------------------------------------------------------

/** Gallery wall dimensions (inches). 12 ft × 8 ft. */
export const WALL_WIDTH = 12 * 12   // 144
export const WALL_HEIGHT = 8 * 12   // 96
/** Frame center height for eye-level hanging (inches from floor). */
export const EYE_LEVEL = 57
/** Ceiling height - lights positioned near here (inches). */
export const CEILING_HEIGHT = WALL_HEIGHT - 4   // 92

/**
 * Create a wall plane to sit behind framed artwork.
 * For gallery layout: use WALL_WIDTH × WALL_HEIGHT, position at (0, wallCenterY, -zOffset).
 *
 * @param {number} width  - wall width in scene units (inches)
 * @param {number} height - wall height in scene units (inches)
 * @param {number|string} color - wall color (hex int or CSS string), default white
 * @param {number} zOffset - how far behind z=0 the wall sits (inches, default 4)
 * @param {number} [centerY] - wall center Y (default 0). Use WALL_HEIGHT/2 for floor-to-ceiling (bottom at y=0).
 * @returns {THREE.Mesh}
 */
export function createWallPlane(width, height, color = 0xffffff, zOffset = 4, centerY = 0) {
  const geo = new THREE.PlaneGeometry(width, height)
  const mat = new THREE.MeshStandardMaterial({
    color: typeof color === 'string' ? new THREE.Color(color) : color,
    roughness: 0.85,
    metalness: 0,
    side: THREE.FrontSide,
  })
  const mesh = new THREE.Mesh(geo, mat)
  mesh.position.set(0, centerY, -zOffset)
  mesh.receiveShadow = true
  mesh.userData = { type: 'wall' }
  return mesh
}

// ---------------------------------------------------------------------------
// Lighting
// ---------------------------------------------------------------------------

/**
 * Create soft ambient fill using a HemisphereLight.
 * Sky = white, ground = warm gray for subtle color variation.
 *
 * @param {number} intensity - light intensity (default 0.5)
 * @returns {THREE.HemisphereLight}
 */
export function createSceneAmbient(intensity = 0.5) {
  return new THREE.HemisphereLight(0xffffff, 0xe8e0d8, intensity)
}

/**
 * Create a subtle directional fill light from upper-left.
 * Does not cast shadows (avoids double-shadow artifacts).
 *
 * @param {number} intensity - light intensity (default 0.15)
 * @returns {THREE.DirectionalLight}
 */
export function createSceneFillLight(intensity = 0.15) {
  const light = new THREE.DirectionalLight(0xffffff, intensity)
  light.position.set(-20, 30, 40)
  light.castShadow = false
  return light
}

/**
 * Create a directional key light that casts shadows onto the wall.
 * Orthographic shadow camera ensures full coverage of frame + wall.
 *
 * @param {number} intensity - light intensity (default 0.4)
 * @param {number} size - half-extent of orthographic shadow frustum (default 80)
 * @returns {THREE.DirectionalLight}
 */
export function createSceneKeyLight(intensity = 0.4, size = 80, ceilingHeight = 92, xOffset = 35) {
  const light = new THREE.DirectionalLight(0xffffff, intensity)
  light.position.set(xOffset, ceilingHeight, 50)
  light.castShadow = true
  light.shadow.mapSize.set(2048, 2048)
  light.shadow.bias = 0.0
  light.shadow.normalBias = 0.02
  light.shadow.camera.left = -size
  light.shadow.camera.right = size
  light.shadow.camera.top = size
  light.shadow.camera.bottom = -size
  light.shadow.camera.near = 1
  light.shadow.camera.far = 120
  light.shadow.camera.updateProjectionMatrix()
  return light
}

/**
 * Create a SpotLight aimed at a specific artwork position.
 * Configured for soft directional shadows cast onto the wall.
 *
 * @param {object} opts
 * @param {THREE.Vector3|{x:number, y:number, z:number}} opts.position - light position
 * @param {THREE.Vector3|{x:number, y:number, z:number}} opts.target   - point light aims at
 * @param {number} opts.intensity  - light intensity (default 0.6)
 * @param {number} opts.angle      - spotlight cone half-angle in radians (default 0.4)
 * @param {number} opts.penumbra   - edge softness 0-1 (default 0.8)
 * @param {number} opts.shadowMapSize - shadow map resolution (default 1024)
 * @returns {THREE.SpotLight}
 */
export function createFrameSpotlight({
  position = { x: 8, y: 16, z: 30 },
  target = { x: 0, y: 0, z: 0 },
  intensity = 0.6,
  angle = 0.4,
  penumbra = 0.8,
  shadowMapSize = 2048,
} = {}) {
  const light = new THREE.SpotLight(0xffffff, intensity, 0, angle, penumbra)
  light.position.set(position.x, position.y, position.z)

  // Shadow configuration
  light.castShadow = true
  light.shadow.mapSize.set(shadowMapSize, shadowMapSize)
  light.shadow.bias = 0.0
  light.shadow.normalBias = 0.02
  light.shadow.camera.near = 1
  light.shadow.camera.far = 200

  // Target object (Three.js SpotLight needs a target in the scene)
  light.target.position.set(target.x, target.y, target.z)

  return light
}

/**
 * Create an NxN grid of shadow-casting directional lights from slightly
 * offset ceiling positions. Shadows overlap tightly on close surfaces
 * (contact shadow stays crisp) but fan apart on distant surfaces
 * (penumbra widens with caster-to-receiver distance), approximating a
 * physically larger area light source without custom shaders.
 *
 * Penumbra width ≈ gridSpacing × distance / (ceilingHeight − centerY)
 *
 * @param {object} opts
 * @param {number} opts.centerX      - scene target X (default 0)
 * @param {number} opts.centerY      - scene target Y (default EYE_LEVEL)
 * @param {number} opts.ceilingHeight - light bank Y position (default CEILING_HEIGHT)
 * @param {number} opts.gridSize     - lights per axis, e.g. 2 → 2×2 (default 2)
 * @param {number} opts.gridSpacing  - inches between lights; wider = faster-spreading penumbra (default 12)
 * @param {number} opts.intensity    - total intensity split evenly across all lights (default 0.4)
 * @param {number} opts.size         - shadow frustum half-extent in inches (default 80)
 * @param {number} opts.shadowMapSize - per-light shadow map resolution (default 2048)
 * @returns {THREE.Light[]} flat [light, light.target, …] array ready for scene.add
 */
export function createDirectionalLightGrid({
  centerX = 0,
  centerY = EYE_LEVEL,
  ceilingHeight = CEILING_HEIGHT,
  gridSize = 2,
  gridSpacing = 12,
  intensity = 0.4,
  size = 80,
  shadowMapSize = 2048,
} = {}) {
  const lights = []
  const count = gridSize * gridSize
  const eachIntensity = intensity / count
  const half = (gridSize - 1) / 2
  const vertOffset = ceilingHeight - centerY

  for (let gx = 0; gx < gridSize; gx++) {
    for (let gy = 0; gy < gridSize; gy++) {
      const ox = (gx - half) * gridSpacing
      const oy = (gy - half) * gridSpacing
      const light = new THREE.DirectionalLight(0xffffff, eachIntensity)
      light.position.set(centerX + vertOffset + ox, ceilingHeight + oy, 50)
      light.target.position.set(centerX, centerY, 0)
      light.castShadow = true
      light.shadow.mapSize.set(shadowMapSize, shadowMapSize)
      light.shadow.bias = 0.0
      light.shadow.normalBias = 0.02
      light.shadow.camera.left = -size
      light.shadow.camera.right = size
      light.shadow.camera.top = size
      light.shadow.camera.bottom = -size
      light.shadow.camera.near = 1
      light.shadow.camera.far = 120
      light.shadow.camera.updateProjectionMatrix()
      lights.push(light)
      lights.push(light.target)
    }
  }

  return lights
}

/**
 * Create the full lighting setup for a single frame.
 * Returns an array of lights to add to the scene.
 *
 * @param {object} opts
 * @param {number} opts.centerX           - horizontal center of the frame (default 0)
 * @param {number} opts.centerY           - vertical center of the frame (default 0)
 * @param {number} opts.ambientIntensity  - hemisphere light intensity
 * @param {number} opts.spotIntensity     - per-frame spotlight intensity
 * @param {number} opts.fillIntensity     - fill light intensity
 * @param {number} opts.keyLightIntensity - key light / grid total intensity
 * @param {number} opts.gridSize          - 0/1 = single directional key light;
 *                                          ≥2 = NxN directional grid for distance-based soft shadows
 * @param {number} opts.gridSpacing       - inches between grid lights (default 12)
 * @returns {THREE.Light[]}
 */
export function createSingleFrameLighting({
  centerX = 0,
  centerY = 0,
  ambientIntensity = 0.5,
  spotIntensity = 0.6,
  fillIntensity = 0.15,
  keyLightIntensity = 0.7,
  ceilingHeight = CEILING_HEIGHT,
  gridSize = 0,
  gridSpacing = 12,
} = {}) {
  const lights = []

  lights.push(createSceneAmbient(ambientIntensity))
  lights.push(createSceneFillLight(fillIntensity))

  const vertOffset = ceilingHeight - centerY

  if (gridSize >= 2) {
    lights.push(...createDirectionalLightGrid({
      centerX,
      centerY,
      ceilingHeight,
      gridSize,
      gridSpacing,
      intensity: keyLightIntensity,
    }))
  } else {
    // Single directional key light — uniform softness regardless of distance.
    const keyLight = createSceneKeyLight(keyLightIntensity, 80, ceilingHeight, centerX + vertOffset)
    keyLight.target.position.set(centerX, centerY, 0)
    lights.push(keyLight)
    lights.push(keyLight.target)
  }

  // Spotlight at ceiling for focused illumination and close-range mat/moulding shadows.
  const spot = createFrameSpotlight({
    position: { x: centerX + vertOffset, y: ceilingHeight, z: 30 },
    target: { x: centerX, y: centerY, z: 0 },
    intensity: spotIntensity,
  })
  lights.push(spot)
  lights.push(spot.target)

  return lights
}

/**
 * Create the scene-level lighting for a multi-frame gallery view.
 * Returns ambient + fill + directional light grid (with shadow cameras).
 * Add per-frame spotlights separately via createFrameSpotlight().
 *
 * @param {object} opts
 * @param {number} opts.ambientIntensity
 * @param {number} opts.fillIntensity
 * @param {number} opts.keyIntensity
 * @param {number} opts.gridSize    - lights per axis (default 2)
 * @param {number} opts.gridSpacing - inches between lights (default 12)
 * @returns {THREE.Light[]}
 */
export function createGallerySceneLighting({
  ambientIntensity = 0.5,
  fillIntensity = 0.15,
  keyIntensity = 0.7,
  gridSize = 1,
  gridSpacing = 12,
} = {}) {
  const lights = []
  lights.push(createSceneAmbient(ambientIntensity))
  lights.push(createSceneFillLight(fillIntensity))
  lights.push(...createDirectionalLightGrid({
    centerX: 0,
    centerY: EYE_LEVEL,
    ceilingHeight: CEILING_HEIGHT,
    gridSize,
    gridSpacing,
    intensity: keyIntensity,
  }))
  return lights
}

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------

/**
 * Create a PerspectiveCamera with telephoto-style narrow FOV.
 * Positioned far from the scene to give flat, compressed perspective
 * similar to product photography with a long lens.
 *
 * @param {number} containerWidth  - viewport width in pixels
 * @param {number} containerHeight - viewport height in pixels
 * @param {number} fov - vertical field of view in degrees (default 18)
 * @returns {THREE.PerspectiveCamera}
 */
export function createTelephotoCamera(containerWidth, containerHeight, fov = 18) {
  const aspect = containerWidth / containerHeight
  const camera = new THREE.PerspectiveCamera(fov, aspect, 0.1, 2000)
  camera.position.set(0, EYE_LEVEL, 100)
  camera.lookAt(0, EYE_LEVEL, 0)
  return camera
}

/**
 * Compute the camera Z distance needed to fit a scene of given dimensions
 * within a perspective camera's view.
 *
 * @param {number} sceneWidth  - scene extent width (scene units)
 * @param {number} sceneHeight - scene extent height (scene units)
 * @param {number} fov         - camera vertical FOV in degrees
 * @param {number} aspect      - camera aspect ratio (width/height)
 * @param {number} padding     - multiplier for breathing room (default 1.05)
 * @returns {number} Z distance from origin
 */
export function computeFitDistance(sceneWidth, sceneHeight, fov, aspect, padding = 1.05) {
  const fovRad = THREE.MathUtils.degToRad(fov)
  const halfH = (sceneHeight * padding) / 2
  const halfW = (sceneWidth * padding) / 2

  // Distance needed to fit height
  const distH = halfH / Math.tan(fovRad / 2)
  // Distance needed to fit width
  const distW = halfW / (Math.tan(fovRad / 2) * aspect)

  return Math.max(distH, distW)
}
